import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid';
import { useFilteredCaseSlideThumbnailsData } from 'components/Procedure/useFilteredCaseIds';
import { ResultsMode } from 'interfaces/experimentResults';
import { UNWOUND_ROW_ID_SEPARATOR, unwoundRowMetadataFromId } from 'interfaces/genericFields/unwindRowsWithInnerArrays';
import { CaseSlideThumbnailData } from 'interfaces/procedure';
import { compact, filter, includes, isEmpty, join, map, uniq } from 'lodash';
import React, { useMemo } from 'react';
import { useClipboard } from 'use-clipboard-copy';
import { BooleanParam, useQueryParam } from 'use-query-params';

import { SelectionMode } from 'components/atoms/RowSelectionContext';
import { CasesParams } from 'utils/useCasesParams/types';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';

const getSlideIdFromRowId = (rowId: GridRowId) => {
  const rowIdAsString = `${rowId}`;

  if (!rowIdAsString.includes(UNWOUND_ROW_ID_SEPARATOR)) {
    return rowIdAsString;
  }

  return `${unwoundRowMetadataFromId(rowIdAsString).innerRowId}`;
};

const getSelectionQueryCasesParams = ({
  labId,
  queryParams,
}: {
  labId?: string;
  queryParams: ReturnType<typeof useEncodedFilters>['queryParams'];
}): CasesParams => ({
  studyId: queryParams.filters?.studyId,
  filters: {
    ...queryParams.filters,
    ...(queryParams.searchTerm ? { searchTerm: queryParams.searchTerm } : {}),
  },
  features: queryParams.features,
  clinicalData: queryParams.clinicalData,
  labId,
  slidesMode: true,
  resultsMode: queryParams.resultsMode || ResultsMode.Manual,
  caseIdsToInclude: queryParams.caseIdsToInclude?.length
    ? map(queryParams.caseIdsToInclude, (caseId) => Number(caseId))
    : undefined,
  slideIdsToInclude: queryParams.slideIdsToInclude?.length ? queryParams.slideIdsToInclude : undefined,
});

export interface SelectedSlidesCopyButtonProps {
  selectedRows: GridRowId[];
  omittedRows: GridRowId[];
  selectionMode: SelectionMode;
  color?: 'primary' | 'warning';
}

const SelectedSlidesCopyButton: React.FC<SelectedSlidesCopyButtonProps> = ({
  selectedRows,
  omittedRows,
  selectionMode,
  color = 'primary',
}) => {
  const [slidesMode] = useQueryParam('slidesMode', BooleanParam);
  const [pendingSlidesMode] = useQueryParam('pendingSlidesMode', BooleanParam);
  const { labId } = useCurrentLabId();
  const { queryParams } = useEncodedFilters();
  const clipboard = useClipboard({ copiedTimeout: 1200 });

  const selectionQueryCasesParams = useMemo(
    () => getSelectionQueryCasesParams({ labId, queryParams }),
    [labId, queryParams]
  );

  const thumbnailsQuery = useFilteredCaseSlideThumbnailsData({
    enabled: Boolean(slidesMode) && selectionMode === 'omit',
    casesParams: selectionQueryCasesParams,
  });

  const filteredSlideThumbnailData = useMemo(
    () =>
      filter(thumbnailsQuery.data || [], ({ caseId }: CaseSlideThumbnailData) =>
        pendingSlidesMode ? caseId == null : caseId !== null
      ),
    [pendingSlidesMode, thumbnailsQuery.data]
  );

  const selectedSlideIds = useMemo(() => {
    if (!slidesMode) {
      return [];
    }

    if (selectionMode === 'select') {
      return uniq(compact(map(selectedRows, getSlideIdFromRowId)));
    }

    const omittedSlideIds = uniq(compact(map(omittedRows, getSlideIdFromRowId)));

    return uniq(
      map(
        filter(filteredSlideThumbnailData, ({ slideId }) => !includes(omittedSlideIds, slideId)),
        'slideId'
      )
    );
  }, [filteredSlideThumbnailData, omittedRows, selectedRows, selectionMode, slidesMode]);

  const isLoading = slidesMode && selectionMode === 'omit' && thumbnailsQuery.isLoading;
  const canCopy = !isLoading && !isEmpty(selectedSlideIds);
  const tooltipTitle = isLoading
    ? 'Loading selected slide IDs'
    : clipboard.copied
    ? 'Copied selected slide IDs'
    : 'Copy selected slide IDs';

  return (
    <Tooltip title={tooltipTitle} arrow>
      <span>
        <IconButton
          size="small"
          color={color}
          aria-label="Copy selected slide IDs"
          data-testid="selected-slides-copy-button"
          disabled={!canCopy}
          onClick={() => {
            clipboard.copy(join(selectedSlideIds, ','));
          }}
          sx={{ flexShrink: 0 }}
        >
          {isLoading ? <CircularProgress size={16} color="inherit" /> : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default SelectedSlidesCopyButton;
